import { Inject, Injectable, Injector } from '@angular/core';
import { coreVendors } from '@nexxe/core-vendors';
import { Observable, Subscription } from 'rxjs';
import { CDSMetadata } from '../class/CDSMetadata';
import { Status } from '../enums/status';
import { ParamsType } from '../interfaces/icds';
import { ICDSConfig } from '../interfaces/icdsconfig';
import { IContext, IRequestOptions } from '../interfaces/irequestmetadata';
import { CdsDataComposerService } from './cds-data-composer.service';
import { CdsRequestService } from './cds-request.service';
import { CdsUtilitiesService } from './cds-utilities.service';

@Injectable({
	providedIn: 'root'
})
export class ConfigurableDataSourceService {
	private get utilities(): CdsUtilitiesService { return this.ngInjector.get(CdsUtilitiesService); }
	private get cdsRequest(): CdsRequestService { return this.ngInjector.get(CdsRequestService); }
	private get cdsComposer(): CdsDataComposerService { return this.ngInjector.get(CdsDataComposerService); }

	constructor(@Inject(Injector) private ngInjector: Injector) {
		CDSMetadata.cdsDataComposer = this.cdsComposer;
	}

	load(config: ICDSConfig, storeId: string = 'default') {
		//this is to fix the signature of the object comes up from from the CDN.
		if (Array.isArray(config.source)) {
			config.source = config.source[0];
		}
		this.utilities.registerMetadata(config, storeId);
	}

	private loadExisting(storeId: string) {
		this.utilities.registerExistingMetaData(storeId);
	}

	unload(storeId: string) {
		this.utilities.removeConfigFromStore(storeId);
	}

	/**
	 * 
	 * @param key CDS ID
	 * @param params params required 
	 * @param payload in case of the call type is post
	 * @param override to inforce invoking a new API everytime
	 */

	request(requestMetadata: IRequestOptions): Observable<any> {
		this.loadExisting(requestMetadata.storeId ? requestMetadata.storeId : 'default');
		return Array.isArray(requestMetadata.keys) ? this.requestMultiple(requestMetadata.keys, <Array<IContext>>requestMetadata.contexts, <Array<boolean>>requestMetadata.override) : this.requestSingle(requestMetadata.keys, requestMetadata.contexts, <boolean>requestMetadata.override);
	}

	private requestSingle(key: string, context: IContext, override?: boolean) {
		const id = this.utilities.createRequest(key, context, false, override);
		this.executeRequest(key, id);
		return this.utilities.getResponse(key, id);
	}

	requestWithFormData(requestMetadata: IRequestOptions) {
		this.loadExisting(requestMetadata.storeId ? requestMetadata.storeId : 'default');
		const id = this.utilities.createRequest(<string>requestMetadata.keys, requestMetadata.contexts, true);
		this.executeRequest(<string>requestMetadata.keys, id);
		return this.utilities.getFormDataResponse(<string>requestMetadata.keys, id);
	}

	private requestMultiple(keys: Array<string>, contexts?: Array<IContext>, override?: Array<boolean>): Observable<any> {
		let ids = [];
		coreVendors.lodash.forEach(keys, (key, index) => {
			const id = this.utilities.createRequest(key, contexts && contexts[index], false, override[index]);
			ids.push(id);
		})
		this.executeRequest(keys, ids);
		return this.utilities.getResponse(keys, ids);
	}

	///TO DO execute
	private executeRequest(keys: string | Array<string>, ids: string | Array<string>) {
		if (typeof ids === 'string' && typeof keys === 'string') {
			this.executeSingleRequest(keys, ids)
		} else if (Array.isArray(keys) && Array.isArray(ids)) {
			this.executeMultipleRequests(keys, ids);
		}
	}

	private executeSingleRequest(key: string, id: string) {
		const status = this.utilities.getMetadataStatus(key, id);
		if (status == Status.success) {
			this.utilities.publishResponse(key, id);
		}
		else if (status == Status.notUsed) {
			this.cdsRequest.execute(<CDSMetadata>this.utilities.getCdsMetadata(key, id));
		}
		else if (status === Status.progress) {
			this.utilities.getInProgressResponse(key, id).subscribe(() => {
				this.utilities.publishResponse(key, id);
			});
		}

	}

	private executeMultipleRequests(keys: Array<string>, ids: Array<string>) {
		const responses = {};
		const cdsMetaDataArray = Array<CDSMetadata>();
		const pendingResponses = Array<{ obs: Observable<any>, key: string, id: string }>();
		coreVendors.lodash.forEach(keys, (key, index) => {
			const status = this.utilities.getMetadataStatus(key, ids[index]);
			if (status == Status.success) {
				responses[key] = this.utilities.getExistingResponse(key, ids[index]);
			}
			else if (status == Status.notUsed) {
				cdsMetaDataArray.push(<CDSMetadata>this.utilities.getCdsMetadata(key, ids[index]));
			}
			else if (status == Status.progress) {
				pendingResponses.push({ obs: this.utilities.getInProgressResponse(key, ids[index]), key, id: ids[index] });
			}
		})
		this.cdsRequest.executeMultiple(cdsMetaDataArray, keys, ids, responses, pendingResponses);
	}

	registerServices({ DmDataModelManagerService, ChannelService }: { [key: string]: Object }) {
		DmDataModelManagerService && (this.cdsComposer.dataModelService = DmDataModelManagerService);
		ChannelService && (this.cdsRequest.channelService = ChannelService);
	}

}
